import { v4 as uuidv4 } from "uuid";

import { ConsentManagementConfigVersion, ConsentModel } from "@/api";
import {
  COUNTRY_REGION_OPTIONS,
  UNDETECTED_COUNTRY_REGION_VALUE,
  undetectedRegionValue,
} from "@/consent-management/components/ConfigPage/ConsentManagerConfigForm/ConfigContent/FreshpaintConfigContent/RegionMultiSelect/constants";
import {
  ConditionalConfig,
  isValidPrivacyPolicyUrl,
  PRIVACY_POLICY_URL_ERROR_MESSAGE,
} from "@/consent-management/components/ConfigPage/ConsentManagerConfigForm/schema";
import { MANAGED_OPT_IN_REGIONS } from "./managedRegions";

export type TargetingPreset =
  | "everywhere"
  | "regions"
  | "sites"
  | "sites-and-regions";

export type RegionTarget = {
  id: string;
  label: string;
  /** ISO 3166-2 subdivision codes, such as `US-CA`. */
  regionValues: string[];
  consentModel: ConsentModel;
};

export type SiteTarget = {
  id: string;
  domain: string;
  consentModel: ConsentModel;
  /** Empty to use the shared privacy policy. */
  privacyPolicyUrl: string;
  /**
   * A match pattern edited on the Consent Manager page that the wizard can't
   * read back as a domain. It's kept until the domain changes.
   */
  customPattern?: { pattern: string; domain: string };
};

export type InactiveRulesChoice = "turn-on" | "remove";

export type ConsentTargeting = {
  preset: TargetingPreset;
  /**
   * Applies to visitors that no region or site matches, which includes
   * visitors whose state or province can't be detected.
   */
  defaultConsentModel: ConsentModel;
  /** Set once the user picks the fallback, so region edits stop changing it. */
  defaultConsentModelChosen: boolean;
  regions: RegionTarget[];
  sites: SiteTarget[];
  /** One banner everywhere switches to Freshpaint's built-in regional rules. */
  useBuiltInRegions?: boolean;
  /**
   * Consent Manager page rules are off unless geotargeting is custom, which
   * targeted presets turn on. Unset until the user chooses.
   */
  inactiveRules?: InactiveRulesChoice;
};

export type GeotargetingBehavior = "disabled" | "managed" | "custom";

/** The ISO codes, which decide whether regions picked a whole country. */
const LISTED_SUBDIVISIONS = new Map(
  COUNTRY_REGION_OPTIONS.map((country) => [
    country.code,
    country.regions
      .filter((region) => region.source === "iso")
      .map((region) => region.value),
  ]),
);

const SUBDIVISIONS_BY_COUNTRY = new Map(
  COUNTRY_REGION_OPTIONS.map((country) => [
    country.code,
    country.regions
      .filter((region) => region.source !== "undetected")
      .map((region) => region.value),
  ]),
);

const isUndetectedValue = (value: string) => value.endsWith("-Unknown");

export const COUNTRY_NAMES = new Map(
  COUNTRY_REGION_OPTIONS.map((country) => [country.code, country.name]),
);

/**
 * Region conditions match the visitor's `COUNTRY-SUBDIVISION` code exactly,
 * so a country is targeted by listing every one of its subdivisions.
 */
export function countriesToRegionValues(countryCodes: string[]): string[] {
  return countryCodes.flatMap(
    (code) => SUBDIVISIONS_BY_COUNTRY.get(code) ?? [],
  );
}

export function regionValuesToCountries(values: string[]): string[] {
  return Array.from(new Set(values.map((value) => value.split("-")[0])));
}

/**
 * The subdivisions managed geotargeting opts in for these countries, read the
 * way the SDK does: a subdivision's own entry wins over the country's `*`.
 */
export function managedOptInValues(countryCodes: string[]): string[] {
  return countryCodes.flatMap((code) => {
    const rules = MANAGED_OPT_IN_REGIONS[code] ?? {};
    return countriesToRegionValues([code]).filter((value) => {
      const subdivision = value.slice(code.length + 1);
      return subdivision in rules
        ? rules[subdivision] === true
        : rules["*"] === true;
    });
  });
}

const US_PRIVACY_STATE_VALUES = managedOptInValues(["US"]);

/** A quick-add choice; the US adds one rule for privacy states and one for the rest. */
export type RegionPreset = {
  id: string;
  label: string;
  regions: RegionTarget[];
};

const EUROPE_LABEL = "EU, UK, Switzerland and territories";

export const REGION_PRESETS: RegionPreset[] = [
  {
    id: "europe",
    label: EUROPE_LABEL,
    regions: [
      {
        id: "europe",
        label: EUROPE_LABEL,
        regionValues: managedOptInValues(
          Object.keys(MANAGED_OPT_IN_REGIONS).filter((code) => code !== "US"),
        ),
        consentModel: "opt-in",
      },
    ],
  },
  {
    id: "us",
    label: "United States",
    regions: [
      {
        id: "us-privacy-states",
        label: "US privacy states",
        regionValues: US_PRIVACY_STATE_VALUES,
        consentModel: "opt-in",
      },
      {
        id: "us-other-states",
        label: "Rest of the US",
        regionValues: countriesToRegionValues(["US"]).filter(
          (value) => !US_PRIVACY_STATE_VALUES.includes(value),
        ),
        consentModel: "opt-out",
      },
    ],
  },
  {
    id: "canada",
    label: "Canada",
    regions: [
      {
        id: "canada",
        label: "Canada",
        regionValues: countriesToRegionValues(["CA"]),
        consentModel: "opt-in",
      },
    ],
  },
  {
    id: "brazil",
    label: "Brazil",
    regions: [
      {
        id: "brazil",
        label: "Brazil",
        regionValues: countriesToRegionValues(["BR"]),
        consentModel: "opt-in",
      },
    ],
  },
];

export const presetUsesRegions = (preset: TargetingPreset) =>
  preset === "regions" || preset === "sites-and-regions";

export const presetUsesSites = (preset: TargetingPreset) =>
  preset === "sites" || preset === "sites-and-regions";

const presetRegions = (id: string) =>
  structuredClone(
    REGION_PRESETS.find((preset) => preset.id === id)?.regions ?? [],
  );

/** Presets whose rules aren't in `regions` yet. */
export const unusedRegionPresets = (regions: RegionTarget[]) =>
  REGION_PRESETS.filter(
    (preset) =>
      !preset.regions.some((presetRegion) =>
        regions.some((region) => region.id === presetRegion.id),
      ),
  );

/**
 * Visitors whose state or province can't be detected fall through to the
 * fallback, so it follows the strictest region until the user picks it.
 */
export function setRegions(
  targeting: ConsentTargeting,
  regions: RegionTarget[],
): ConsentTargeting {
  const next = { ...targeting, regions };
  if (
    !next.defaultConsentModelChosen &&
    regions.some((region) => region.consentModel === "opt-in")
  ) {
    next.defaultConsentModel = "opt-in";
  }
  return next;
}

export const addRegionPreset = (targeting: ConsentTargeting, id: string) =>
  setRegions(targeting, [...targeting.regions, ...presetRegions(id)]);

export const chooseDefaultConsentModel = (
  targeting: ConsentTargeting,
  defaultConsentModel: ConsentModel,
): ConsentTargeting => ({
  ...targeting,
  defaultConsentModel,
  defaultConsentModelChosen: true,
});

/**
 * Switches presets without losing targets the user already entered. A preset
 * that needs regions or sites and has none gets starting rows: regional rules
 * start with the protections managed geotargeting gives.
 */
export function changePreset(
  targeting: ConsentTargeting,
  preset: TargetingPreset,
  newId: () => string,
): ConsentTargeting {
  let next = { ...targeting, preset };
  if (presetUsesRegions(preset) && next.regions.length === 0) {
    next = setRegions(next, [
      ...presetRegions("europe"),
      ...presetRegions("us"),
    ]);
  }
  if (presetUsesSites(preset) && next.sites.length === 0) {
    next.sites = [newSite(newId(), next.defaultConsentModel)];
  }
  return next;
}

/** Short, since target IDs end up in config IDs and stylesheet paths. */
export const newTargetId = () => uuidv4().replace(/-/g, "").slice(0, 8);

export function newSite(id: string, consentModel: ConsentModel): SiteTarget {
  return { id, domain: "", consentModel, privacyPolicyUrl: "" };
}

/** Accepts a hostname or a pasted URL and returns its bare hostname. */
export function normalizeDomain(input: string): string {
  return input
    .trim()
    .toLowerCase()
    .replace(/^[a-z]+:\/\//, "")
    .replace(/[/?#:].*$/, "")
    .replace(/^(\*\.|www\.)/, "")
    .replace(/\.$/, "");
}

const DOMAIN_REGEX = /^([a-z0-9-]+\.)+[a-z0-9-]{2,}$/;

export const isValidDomain = (domain: string) => DOMAIN_REGEX.test(domain);

const escapeRegex = (value: string) =>
  value.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");

const DOMAIN_PATTERN_PREFIX = "^https?://([a-z0-9-]+\\.)*";
const DOMAIN_PATTERN_SUFFIX = "(:\\d+)?([/?#]|$)";

/**
 * The SDK tests domain conditions against the full page URL, so the pattern
 * anchors on the host and also matches its subdomains (www, shop, ...).
 */
export function domainToPattern(domain: string): string {
  return `${DOMAIN_PATTERN_PREFIX}${escapeRegex(domain)}${DOMAIN_PATTERN_SUFFIX}`;
}

export function patternToDomain(pattern: string): string | null {
  if (
    !pattern.startsWith(DOMAIN_PATTERN_PREFIX) ||
    !pattern.endsWith(DOMAIN_PATTERN_SUFFIX)
  ) {
    return null;
  }
  return pattern
    .slice(DOMAIN_PATTERN_PREFIX.length, -DOMAIN_PATTERN_SUFFIX.length)
    .replace(/\\(.)/g, "$1");
}

export type TargetingError = { targetId: string | null; message: string };

export function getTargetingErrors(
  targeting: ConsentTargeting,
  inactiveRuleCount = 0,
): TargetingError[] {
  const errors: TargetingError[] = [];
  if (targeting.preset === "everywhere") {
    return errors;
  }

  if (inactiveRuleCount > 0 && !targeting.inactiveRules) {
    errors.push({
      targetId: null,
      message: `Choose whether to turn on or remove the ${inactiveRuleCount} inactive ${inactiveRuleCount === 1 ? "rule" : "rules"}`,
    });
  }

  if (presetUsesRegions(targeting.preset)) {
    if (targeting.regions.length === 0) {
      errors.push({ targetId: null, message: "Add at least one region" });
    }
    const ownerByValue = new Map<string, string>();
    for (const region of targeting.regions) {
      const label = region.label.trim();
      if (!label) {
        errors.push({ targetId: region.id, message: "Name this region" });
      }
      if (region.regionValues.length === 0) {
        errors.push({
          targetId: region.id,
          message: `Pick at least one country for ${label || "this region"}`,
        });
      }
      const overlaps = new Map<string, string>();
      for (const value of region.regionValues) {
        const owner = ownerByValue.get(value);
        if (owner !== undefined) {
          overlaps.set(value.split("-")[0], owner);
        } else {
          ownerByValue.set(value, label || "another region");
        }
      }
      for (const [code, owner] of overlaps) {
        errors.push({
          targetId: region.id,
          message: `${COUNTRY_NAMES.get(code) ?? code} is already in ${owner}`,
        });
      }
    }
  }

  if (presetUsesSites(targeting.preset)) {
    if (targeting.sites.length === 0) {
      errors.push({ targetId: null, message: "Add at least one site" });
    }
    const seen = new Set<string>();
    for (const site of targeting.sites) {
      const domain = normalizeDomain(site.domain);
      if (!keepsCustomPattern(site) && !isValidDomain(domain)) {
        errors.push({
          targetId: site.id,
          message: "Enter a domain like example.com",
        });
        continue;
      }
      if (seen.has(domain)) {
        errors.push({
          targetId: site.id,
          message: `${domain} is listed more than once`,
        });
      }
      seen.add(domain);
      if (!isValidPrivacyPolicyUrl(site.privacyPolicyUrl.trim())) {
        errors.push({
          targetId: site.id,
          message: PRIVACY_POLICY_URL_ERROR_MESSAGE,
        });
      }
    }
  }

  return errors;
}

const WIZARD_ID_PREFIX = "wizard-";
const REGION_ID_PREFIX = `${WIZARD_ID_PREFIX}region-`;
const SITE_ID_PREFIX = `${WIZARD_ID_PREFIX}site-`;
const CROSS_ID_SEPARATOR = "-region-";

export const isWizardConfig = (config: Pick<ConditionalConfig, "id">) =>
  config.id.startsWith(WIZARD_ID_PREFIX);

const isCombinedConfigId = (id: string) =>
  id.startsWith(SITE_ID_PREFIX) && id.includes(CROSS_ID_SEPARATOR);

const regionConfigId = (region: RegionTarget) =>
  `${REGION_ID_PREFIX}${region.id}`;
const siteConfigId = (site: SiteTarget) => `${SITE_ID_PREFIX}${site.id}`;
const crossConfigId = (site: SiteTarget, region: RegionTarget) =>
  `${SITE_ID_PREFIX}${site.id}${CROSS_ID_SEPARATOR}${region.id}`;

type WizardOwnedFields = Pick<
  ConditionalConfig,
  "id" | "label" | "matchConditions" | "consentModel" | "privacyPolicyUrl"
>;

type FreshpaintSlot = Partial<
  Pick<
    ConditionalConfig,
    | "customCSS"
    | "language"
    | "guiOptions"
    | "autoShow"
    | "disablePageInteraction"
    | "hideConsentModalRejectAll"
    | "enableConsentModalDismiss"
    | "editSelections"
    | "categorySettings"
  >
> & {
  geotargetingBehavior?: GeotargetingBehavior;
  conditionalConfigs?: ConditionalConfig[];
};

/**
 * The values each region's condition matches. A country the regions cover in
 * full also gets the codes the subdivision list lacks and `XX-Unknown`, for
 * visitors whose subdivision can't be detected. When several regions split
 * the country, those go to the strictest one. `US-Unknown` also holds visitors
 * whose country can't be detected, so only an opt-in region gets it; otherwise
 * they keep the default consent model.
 */
export function regionMatchValues(
  regions: RegionTarget[],
): Map<string, string[]> {
  const values = new Map(
    regions.map((region) => [region.id, [...region.regionValues]]),
  );
  const covered = new Set(regions.flatMap((region) => region.regionValues));
  for (const code of regionValuesToCountries([...covered])) {
    const listed = LISTED_SUBDIVISIONS.get(code) ?? [];
    if (!listed.every((value) => covered.has(value))) {
      continue;
    }
    const holders = regions.filter((region) =>
      region.regionValues.some((value) => value.startsWith(`${code}-`)),
    );
    const optIn = holders.find((region) => region.consentModel === "opt-in");
    const owner = optIn ?? holders[0];
    const undetected = undetectedRegionValue(code);
    const extra = [
      ...(SUBDIVISIONS_BY_COUNTRY.get(code) ?? []),
      ...(optIn || undetected !== UNDETECTED_COUNTRY_REGION_VALUE
        ? [undetected]
        : []),
    ].filter((value) => !covered.has(value));
    values.get(owner.id)?.push(...extra);
  }
  return values;
}

/**
 * Countries an opt-in region only partly covers. Visitors there whose
 * subdivision can't be detected match none of the regions.
 */
export function undetectedGaps(targeting: ConsentTargeting): string[] {
  if (!presetUsesRegions(targeting.preset)) {
    return [];
  }
  const matched = new Set(
    [...regionMatchValues(targeting.regions).values()].flat(),
  );
  return regionValuesToCountries(
    targeting.regions
      .filter((region) => region.consentModel === "opt-in")
      .flatMap((region) => region.regionValues),
  ).filter((code) => !matched.has(undetectedRegionValue(code)));
}

export const keepsCustomPattern = (site: SiteTarget) =>
  !!site.customPattern &&
  normalizeDomain(site.customPattern.domain) === normalizeDomain(site.domain);

const sitePattern = (site: SiteTarget) =>
  site.customPattern && keepsCustomPattern(site)
    ? site.customPattern.pattern
    : domainToPattern(site.domain);

function wizardOwnedFields(targeting: ConsentTargeting): WizardOwnedFields[] {
  const regions = presetUsesRegions(targeting.preset) ? targeting.regions : [];
  const sites = presetUsesSites(targeting.preset)
    ? [...targeting.sites]
        .map((site) => ({ ...site, domain: normalizeDomain(site.domain) }))
        // Equally specific configs resolve in array order, so a subdomain has
        // to come before the parent domain that also matches it.
        .sort((a, b) => b.domain.split(".").length - a.domain.split(".").length)
    : [];
  const matchValues = regionMatchValues(regions);

  const regionCondition = (region: RegionTarget) => ({
    type: "region",
    values: matchValues.get(region.id) ?? region.regionValues,
  });
  const domainCondition = (site: SiteTarget) => ({
    type: "domain",
    values: [sitePattern(site)],
  });

  // A site rule outranks a region rule because it comes first, so only an
  // opt-out site needs a site-by-region rule for the stricter opt-in region.
  return [
    ...sites
      .filter((site) => site.consentModel === "opt-out")
      .flatMap((site) =>
        regions
          .filter((region) => region.consentModel === "opt-in")
          .map((region) => ({
            id: crossConfigId(site, region),
            label: `${site.domain} · ${region.label.trim()}`,
            matchConditions: [domainCondition(site), regionCondition(region)],
            consentModel: "opt-in" as const,
            privacyPolicyUrl: site.privacyPolicyUrl.trim(),
          })),
      ),
    ...sites.map((site) => ({
      id: siteConfigId(site),
      label: site.domain,
      matchConditions: [domainCondition(site)],
      consentModel: site.consentModel,
      privacyPolicyUrl: site.privacyPolicyUrl.trim(),
    })),
    ...regions.map((region) => ({
      id: regionConfigId(region),
      label: region.label.trim(),
      matchConditions: [regionCondition(region)],
      consentModel: region.consentModel,
      privacyPolicyUrl: "",
    })),
  ];
}

/**
 * New targeted configs start as copies of the global banner, so copy, layout
 * and category behavior are shared until someone edits a target on the
 * Consent Manager page. Re-running the wizard only rewrites the fields it
 * owns and keeps those edits.
 */
function toConditionalConfig(
  owned: WizardOwnedFields,
  existing: ConditionalConfig | undefined,
  global: FreshpaintSlot,
): ConditionalConfig {
  if (existing) {
    return { ...existing, ...owned };
  }
  return {
    ...owned,
    customCSS: global.customCSS ?? "",
    language: structuredClone(global.language) as ConditionalConfig["language"],
    guiOptions: structuredClone(
      global.guiOptions,
    ) as ConditionalConfig["guiOptions"],
    autoShow: global.autoShow ?? true,
    disablePageInteraction: global.disablePageInteraction ?? false,
    hideConsentModalRejectAll: global.hideConsentModalRejectAll ?? false,
    enableConsentModalDismiss: global.enableConsentModalDismiss ?? false,
    editSelections: global.editSelections ?? false,
    categorySettings: structuredClone(global.categorySettings ?? {}),
    disabled: false,
    privacyPolicyLinkText: "",
  };
}

const isCustom = (global: FreshpaintSlot) =>
  global.geotargetingBehavior === "custom";

/** Rules made on the Consent Manager page, which apply only while custom. */
const pageRules = (global: FreshpaintSlot) =>
  (global.conditionalConfigs ?? []).filter((config) => !isWizardConfig(config));

/**
 * Writes the targeting choice into a draft version. Targeted configs created
 * outside the wizard stay first, so they keep winning ties.
 */
export function applyTargetingToDraft(
  draft: ConsentManagementConfigVersion,
  targeting: ConsentTargeting,
): ConsentManagementConfigVersion {
  const global: FreshpaintSlot = draft.freshpaintConfig ?? {};
  const current = global.conditionalConfigs ?? [];
  const others = pageRules(global);
  const existingById = new Map(current.map((config) => [config.id, config]));

  if (targeting.preset === "everywhere") {
    const currentBehavior = global.geotargetingBehavior ?? "disabled";
    return {
      ...draft,
      freshpaintConfig: {
        ...global,
        conditionalConfigs: others,
        geotargetingBehavior: targeting.useBuiltInRegions
          ? "managed"
          : currentBehavior === "custom"
            ? others.length > 0
              ? "custom"
              : "managed"
            : currentBehavior,
      },
    };
  }

  const keptOthers =
    !isCustom(global) && targeting.inactiveRules === "remove" ? [] : others;
  return {
    ...draft,
    consentModel: targeting.defaultConsentModel,
    freshpaintConfig: {
      ...global,
      geotargetingBehavior: "custom",
      conditionalConfigs: [
        ...keptOthers,
        ...wizardOwnedFields(targeting).map((owned) =>
          toConditionalConfig(owned, existingById.get(owned.id), global),
        ),
      ],
    },
  };
}

function fnv1a(value: string): string {
  let hash = 0x811c9dc5;
  for (let i = 0; i < value.length; i++) {
    hash ^= value.charCodeAt(i);
    hash = Math.imul(hash, 0x01000193);
  }
  return (hash >>> 0).toString(36);
}

/**
 * Identifies the parts of a draft that targeting reads or replaces, so edits
 * made against it can be told stale. Null when there is no draft yet.
 */
export function draftFingerprint(
  draft: ConsentManagementConfigVersion | undefined,
): string | null {
  if (!draft) {
    return null;
  }
  const global: FreshpaintSlot = draft.freshpaintConfig ?? {};
  return fnv1a(
    JSON.stringify([
      draft.consentModel,
      global.geotargetingBehavior ?? null,
      global.conditionalConfigs ?? [],
    ]),
  );
}

export type ConsentDraftState = {
  draft: ConsentManagementConfigVersion | undefined;
  /** False while the draft loads or after it failed to; `draft` is then meaningless. */
  known: boolean;
};

/**
 * The targeting edits stored in the session, unless the draft's targets have
 * changed since they were made, in which case the draft's own targets. Stale
 * edits would overwrite changes made on the Consent Manager page since.
 * Stored edits stand while the draft is unknown, and nothing here writes them,
 * so a failed load can't replace them.
 */
export function currentTargeting(
  data: {
    targeting: ConsentTargeting | null;
    targetingDraft?: string | null;
    consentModel: ConsentModel;
  },
  { draft, known }: ConsentDraftState,
): ConsentTargeting {
  const fresh =
    !known || (data.targetingDraft ?? null) === draftFingerprint(draft);
  return data.targeting && fresh
    ? data.targeting
    : deriveTargeting(draft, data.consentModel);
}

/** Reads the wizard's targets back out of a saved draft. */
export function deriveTargeting(
  draft: ConsentManagementConfigVersion | undefined,
  fallbackConsentModel: ConsentModel,
): ConsentTargeting {
  const global: FreshpaintSlot = draft?.freshpaintConfig ?? {};
  const configs =
    global.geotargetingBehavior === "custom"
      ? (global.conditionalConfigs ?? []).filter(isWizardConfig)
      : [];

  const regions: RegionTarget[] = [];
  const sites: SiteTarget[] = [];
  for (const config of configs) {
    const values = (type: string) =>
      config.matchConditions.find((condition) => condition.type === type)
        ?.values ?? [];
    if (config.id.startsWith(REGION_ID_PREFIX)) {
      regions.push({
        id: config.id.slice(REGION_ID_PREFIX.length),
        label: config.label,
        regionValues: values("region").filter(
          (value) => !isUndetectedValue(value),
        ),
        consentModel: config.consentModel,
      });
    } else if (!isCombinedConfigId(config.id)) {
      const pattern = values("domain")[0] ?? "";
      const domain = patternToDomain(pattern);
      sites.push({
        id: config.id.slice(SITE_ID_PREFIX.length),
        domain: domain || config.label,
        consentModel: config.consentModel,
        privacyPolicyUrl: config.privacyPolicyUrl ?? "",
        ...(!domain && pattern
          ? { customPattern: { pattern, domain: config.label } }
          : {}),
      });
    }
  }

  const preset: TargetingPreset =
    regions.length > 0 && sites.length > 0
      ? "sites-and-regions"
      : regions.length > 0
        ? "regions"
        : sites.length > 0
          ? "sites"
          : "everywhere";

  return {
    preset,
    defaultConsentModel: draft?.consentModel ?? fallbackConsentModel,
    defaultConsentModelChosen: preset !== "everywhere",
    regions,
    sites,
  };
}

/** Active targeted configs someone created on the Consent Manager page. */
export function countOtherTargetedConfigs(
  draft: ConsentManagementConfigVersion | undefined,
): number {
  const global: FreshpaintSlot = draft?.freshpaintConfig ?? {};
  return isCustom(global) ? pageRules(global).length : 0;
}

/**
 * Consent Manager page rules that are off because geotargeting isn't custom.
 * Saving targeted rules turns custom geotargeting on, and them with it.
 */
export function countInactiveRules(
  draft: ConsentManagementConfigVersion | undefined,
): number {
  const global: FreshpaintSlot = draft?.freshpaintConfig ?? {};
  return isCustom(global) ? 0 : pageRules(global).length;
}

/** Wizard targets whose banner was turned off on the Consent Manager page. */
export function bannerOffTargetIds(
  draft: ConsentManagementConfigVersion | undefined,
): Set<string> {
  const global: FreshpaintSlot = draft?.freshpaintConfig ?? {};
  return new Set(
    (global.conditionalConfigs ?? [])
      .filter(
        (config) =>
          config.disabled &&
          (config.id.startsWith(REGION_ID_PREFIX) ||
            (config.id.startsWith(SITE_ID_PREFIX) &&
              !isCombinedConfigId(config.id))),
      )
      .map((config) =>
        config.id.startsWith(REGION_ID_PREFIX)
          ? config.id.slice(REGION_ID_PREFIX.length)
          : config.id.slice(SITE_ID_PREFIX.length),
      ),
  );
}

export type OverridingRule = {
  rule: string;
  target: string;
  /** The page rule turns the banner off rather than asking for opt-out. */
  bannerOff: boolean;
};

const conditionValues = (config: Pick<ConditionalConfig, "matchConditions">) =>
  Object.fromEntries(
    config.matchConditions.map((condition) => [
      condition.type,
      condition.values,
    ]),
  ) as Partial<Record<string, string[]>>;

/**
 * Consent Manager page rules that win over an opt-in wizard rule for some of
 * its visitors and give them opt-out or no banner. Page rules come first, so
 * they win ties as well as being more specific.
 */
export function findOverridingRules(
  draft: ConsentManagementConfigVersion | undefined,
  targeting: ConsentTargeting,
): OverridingRule[] {
  const global: FreshpaintSlot = draft?.freshpaintConfig ?? {};
  if (
    targeting.preset === "everywhere" ||
    (!isCustom(global) && targeting.inactiveRules !== "turn-on")
  ) {
    return [];
  }
  const found: OverridingRule[] = [];
  for (const owned of wizardOwnedFields(targeting)) {
    if (owned.consentModel !== "opt-in") {
      continue;
    }
    const wizardValues = conditionValues(owned);
    for (const rule of pageRules(global)) {
      if (
        rule.matchConditions.length < owned.matchConditions.length ||
        (rule.consentModel !== "opt-out" && !rule.disabled)
      ) {
        continue;
      }
      const ruleValues = conditionValues(rule);
      const regionsOverlap =
        !ruleValues.region ||
        !wizardValues.region ||
        ruleValues.region.some((value) => wizardValues.region?.includes(value));
      const wizardPattern = wizardValues.domain?.[0];
      const wizardDomain = wizardPattern
        ? (patternToDomain(wizardPattern) ?? owned.label)
        : null;
      const domainsOverlap =
        !ruleValues.domain ||
        !wizardDomain ||
        ruleValues.domain.some((pattern) => {
          try {
            return new RegExp(pattern).test(`https://${wizardDomain}/`);
          } catch {
            return false;
          }
        });
      if (regionsOverlap && domainsOverlap) {
        found.push({
          rule: rule.label,
          target: owned.label,
          bannerOff: rule.disabled,
        });
      }
    }
  }
  return found;
}

export type TargetSummary = {
  id: string;
  label: string;
  consentModel: ConsentModel;
  matches: string;
  /** A site-by-region config the wizard derives from a site and a region. */
  combined: boolean;
  /** Turned off on the Consent Manager page: matching visitors see no banner. */
  bannerOff: boolean;
};

const MAX_LISTED_COUNTRIES = 3;

export function describeRegionValues(values: string[]): string {
  const selected = new Set(values);
  const names = regionValuesToCountries(values).map((code) => {
    const name = COUNTRY_NAMES.get(code) ?? code;
    const subdivisions = SUBDIVISIONS_BY_COUNTRY.get(code) ?? [];
    const count = subdivisions.filter((value) => selected.has(value)).length;
    return count > 0 && count < subdivisions.length
      ? `${name} (${count} ${count === 1 ? "region" : "regions"})`
      : name;
  });
  if (names.length <= MAX_LISTED_COUNTRIES) {
    return names.join(", ");
  }
  return `${names.slice(0, MAX_LISTED_COUNTRIES).join(", ")} and ${
    names.length - MAX_LISTED_COUNTRIES
  } more`;
}

/** The targeted configs a published draft would apply, for review. */
export function summarizeTargets(
  draft: ConsentManagementConfigVersion | undefined,
): TargetSummary[] {
  const global: FreshpaintSlot = draft?.freshpaintConfig ?? {};
  if (global.geotargetingBehavior !== "custom") {
    return [];
  }
  return (global.conditionalConfigs ?? [])
    .filter((config) => config.matchConditions.length > 0)
    .map((config) => ({
      id: config.id,
      label: config.label,
      consentModel: config.consentModel,
      matches: config.matchConditions
        .map((condition) =>
          condition.type === "region"
            ? describeRegionValues(condition.values)
            : condition.values
                .map((pattern) => patternToDomain(pattern) ?? pattern)
                .join(", "),
        )
        .join(" · "),
      combined: isCombinedConfigId(config.id),
      bannerOff: config.disabled,
    }));
}

/** Whether saving `targeting` would change the draft. */
export function targetingChangesDraft(
  draft: ConsentManagementConfigVersion,
  targeting: ConsentTargeting,
): boolean {
  const next = applyTargetingToDraft(draft, targeting);
  return (
    next.consentModel !== draft.consentModel ||
    next.freshpaintConfig?.geotargetingBehavior !==
      draft.freshpaintConfig?.geotargetingBehavior ||
    JSON.stringify(next.freshpaintConfig?.conditionalConfigs ?? []) !==
      JSON.stringify(draft.freshpaintConfig?.conditionalConfigs ?? [])
  );
}
